import React, { useEffect, useId, useRef, useState } from 'react';
import { CANADA_PROVINCE_CODES, buildCreateAdminProspectRequest } from '../../services/admin/prospectsListRequest';
import { createAdminProspect } from '../../services/admin/prospectsService';
import {
  adminProspectCreateErrorKey,
  classifyAdminCallableError,
} from './adminProspectDisplay';

const EMPTY_FORM = {
  address: '',
  city: '',
  province: '',
  postalCode: '',
  numberOfUnits: '',
  ownerName: '',
  ownerMailingAddress: '',
  notes: '',
};

/**
 * Manual create dialog. Backend owns source, status, and duplicate policy.
 */
export default function AdminCreateProspectDialog({ open, t, onClose, onCreated }) {
  const titleId = useId();
  const errorId = useId();
  const addressRef = useRef(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setSaving(false);
    setError('');
    const id = window.setTimeout(() => addressRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) {
        event.preventDefault();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, saving, onClose]);

  if (!open) return null;

  const requiredReady =
    form.address.trim() && form.city.trim() && form.province.trim() && form.postalCode.trim();

  const setField = (name) => (event) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!requiredReady || saving) return;
    setSaving(true);
    setError('');
    const payload = buildCreateAdminProspectRequest(form);
    try {
      await createAdminProspect(payload);
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(t(adminProspectCreateErrorKey(classifyAdminCallableError(err))));
      setSaving(false);
    }
  };

  const fieldClass =
    'mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-lg">
        <h2 id={titleId} className="text-lg font-semibold text-slate-900">
          {t('admin.prospection.create.title')}
        </h2>
        <form
          onSubmit={handleSubmit}
          className="mt-4 space-y-3"
          noValidate
          aria-describedby={error ? errorId : undefined}
        >
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-address">
            {t('admin.prospection.field.address')}
            <input
              ref={addressRef}
              id="admin-prospect-address"
              name="address"
              type="text"
              required
              autoComplete="off"
              value={form.address}
              disabled={saving}
              onChange={setField('address')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-city">
            {t('admin.prospection.field.city')}
            <input
              id="admin-prospect-city"
              name="city"
              type="text"
              required
              autoComplete="off"
              value={form.city}
              disabled={saving}
              onChange={setField('city')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-province">
            {t('admin.prospection.field.province')}
            <select
              id="admin-prospect-province"
              name="province"
              required
              value={form.province}
              disabled={saving}
              onChange={setField('province')}
              className={fieldClass}
            >
              <option value="">{t('admin.prospection.province.placeholder')}</option>
              {CANADA_PROVINCE_CODES.map((code) => (
                <option key={code} value={code}>
                  {t(`admin.prospection.province.${code}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-postal">
            {t('admin.prospection.field.postalCode')}
            <input
              id="admin-prospect-postal"
              name="postalCode"
              type="text"
              required
              autoComplete="off"
              value={form.postalCode}
              disabled={saving}
              onChange={setField('postalCode')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-units">
            {t('admin.prospection.field.units')}
            <input
              id="admin-prospect-units"
              name="numberOfUnits"
              type="number"
              min="0"
              step="1"
              inputMode="numeric"
              value={form.numberOfUnits}
              disabled={saving}
              onChange={setField('numberOfUnits')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-owner-name">
            {t('admin.prospection.field.ownerName')}
            <input
              id="admin-prospect-owner-name"
              name="ownerName"
              type="text"
              autoComplete="off"
              value={form.ownerName}
              disabled={saving}
              onChange={setField('ownerName')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-owner-mailing">
            {t('admin.prospection.field.ownerMailingAddress')}
            <textarea
              id="admin-prospect-owner-mailing"
              name="ownerMailingAddress"
              rows={2}
              autoComplete="off"
              value={form.ownerMailingAddress}
              disabled={saving}
              onChange={setField('ownerMailingAddress')}
              className={fieldClass}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700" htmlFor="admin-prospect-notes">
            {t('admin.prospection.field.notes')}
            <textarea
              id="admin-prospect-notes"
              name="notes"
              rows={3}
              value={form.notes}
              disabled={saving}
              onChange={setField('notes')}
              className={fieldClass}
            />
          </label>
          {error ? (
            <p id={errorId} className="text-sm text-red-700" role="alert">
              {error}
            </p>
          ) : null}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              disabled={saving}
              onClick={onClose}
            >
              {t('admin.prospection.create.cancel')}
            </button>
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
              disabled={saving || !requiredReady}
            >
              {saving ? t('admin.prospection.create.submitting') : t('admin.prospection.create.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
